import React from 'react';

import { BackNavigation, LayoutSingleColumn, NamedLink, Page } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  SellerResourceHeader,
  SellerResourceCTA,
  SellerResourceLayout,
  FeesGuide,
  ShippingGuide,
  ConditionGuide,
  GettingPaidGuide,
  SellerFAQGuide,
  ShopGuide,
} from './SellerResources';
import SellingOverview from './SellingOverview';
import WhenSomethingGoesWrong from './WhenSomethingGoesWrong';

import css from './HowToUseShelfyPage.module.css';

const NewListingLink = ({ children, className, search }) => (
  <NamedLink className={className} name="NewListingPage" to={{ search }}>
    {children}
  </NamedLink>
);

const topics = [
  [
    'buying',
    'Buying on PageHop',
    'How to browse, buy, and manage your purchases.',
    'Browse and buy',
  ],
  [
    'selling',
    'Selling on PageHop',
    'How to create listings, manage sales, and get paid.',
    'Create and sell',
  ],
  [
    'seller-shops',
    'Manage Your Shop',
    'How your PageHop Shop works and what buyers see.',
    'Your storefront',
  ],
  [
    'iso',
    'In Search Of',
    "Post what you're looking for when you can't find it listed.",
    'Make a request',
  ],
  [
    'my-shelf',
    'My Shelf',
    'Keep track of your collection, listings, ISOs, and wishlist.',
    'Stay organized',
  ],
  [
    'shipping',
    'Shipping & Delivery',
    'What buyers and sellers need to know about shipping within Canada.',
    'Ship with care',
  ],
  [
    'payments',
    'Fees',
    'Simple seller fees, payouts, and what to expect when an item sells.',
    'Fees',
  ],
  [
    'condition',
    'Condition Guide',
    'Use PageHop condition standards so buyers know what to expect.',
    'Describe condition',
  ],
  [
    'getting-paid',
    'Getting Paid',
    'A clear guide to payout setup, delivery, and the 48-hour hold.',
    'Manage payouts',
  ],
  ['seller-faqs', 'Seller FAQs', 'Quick answers for common selling questions.', 'Find answers'],
  [
    'trust',
    'Trust & Safety',
    'How reviews, accurate listings, and on-platform transactions help keep PageHop safer.',
    'Trade thoughtfully',
  ],
  [
    'account',
    'Account & Profile',
    'Manage your account, public profile, Shop settings, and preferences.',
    'Your details',
  ],
];

const articles = {
  buying: [
    'Buying on PageHop',
    'Find books and bookish goods from Canadian sellers.',
    [
      [
        'Browse and search',
        'Use marketplace search to browse by title, author, genre, and edition details where available. You can also visit a Seller Shop.',
      ],
      [
        'Read the listing',
        'Review the title, author, condition, photos, disclosed flaws, relevant edition information, seller details, price, and shipping information.',
      ],
      [
        'Buy through PageHop',
        "Use a listing's purchase action to continue through PageHop's existing checkout flow.",
      ],
      [
        'Track your purchase',
        'Transaction information and order messages remain available through PageHop.',
      ],
    ],
  ],
  selling: [
    'Selling on PageHop',
    'Create a clear listing, price it fairly, ship it carefully, and manage the sale from My Shelf.',
    [],
  ],
  'seller-shops': [
    'Manage Your Shop',
    'Every seller has a public PageHop Shop tied to their account.',
    [],
  ],
  iso: [
    'In Search Of',
    'Post a request when the book or edition you want is not listed.',
    [
      ['What an ISO is', 'An ISO is an active request for something you want to buy.'],
      [
        'When to use it',
        'Use an ISO instead of a wishlist when you are actively looking for an item.',
      ],
      [
        'What to include',
        'Include the title, author, budget, edition, condition, signatures, extras, and shipping preferences where they matter.',
      ],
      ['Seller responses', 'Sellers can use the existing listing and messaging tools to respond.'],
    ],
  ],
  'my-shelf': [
    'My Shelf',
    'Keep your PageHop activity together in one account area.',
    [
      ['My Books', 'The books you own, shelved by genre.'],
      ['Selling', 'Books you are ready to rehome and your active listings.'],
      ['In Search Of and Wishlist', 'Your ISO posts and listings you want to revisit.'],
    ],
  ],
  shipping: [
    'Shipping & Delivery',
    'A practical guide to packing, tracking, and sending PageHop orders within Canada.',
    [
      [
        'Pack it well',
        'Sellers are responsible for making sure books and bookish goods are packed securely and protected for the trip.\n\nUse packaging that makes sense for the item, especially for special editions, dust jackets, sprayed edges, or loose extras.',
      ],
      [
        'Shipping details',
        'Shipping costs and delivery information are shown on the listing and at checkout where applicable. The buyer pays the shipping cost as part of their order.\n\nCheck the order details before shipping so you know which service and delivery terms apply.',
      ],
      [
        'Tracking',
        'Add tracking when the shipping service supports it.\n\nTracking helps both buyers and sellers follow the order and makes delivery easier to confirm.',
      ],
      [
        'Shipping within Canada',
        'PageHop is built for Canadian buyers and sellers.\n\nSellers provide the package details needed for shipping and handle fulfillment. PageHop does not currently use fixed national shipping rates or integrated shipping labels.',
      ],
      [
        'Keep buyers in the loop',
        'Once an order is on the way, add the relevant shipping or tracking information through the transaction flow where available.\n\nClear shipping updates help keep the sale simple from checkout to delivery.',
      ],
    ],
  ],
  payments: [
    'Fees',
    'PageHop keeps seller fees simple. Listing is free, and you only pay when something sells.',
    [],
  ],
  condition: [
    'Condition Guide',
    'Use clear, consistent condition information so buyers understand the item before purchasing.',
    [
      [
        'Describe the item',
        'Use the applicable PageHop condition standard and describe the actual item being sold.',
      ],
      [
        'Disclose relevant details',
        'Mention wear, marks, loose extras, and other details a buyer would reasonably want to know before purchasing.',
      ],
      [
        'Use clear photos',
        'Show the item, its condition, and any relevant edition details clearly in your listing photos.',
      ],
    ],
  ],
  'getting-paid': [
    'Getting Paid',
    'A clear guide to payout setup, delivery, and the 48-hour hold.',
    [
      [
        'Set up your payout details',
        'Complete your payout details in your account so PageHop can release your earnings through the configured payout system.',
      ],
      [
        'After delivery',
        'Once an order is marked delivered, the sale proceeds are held for 48 hours before earnings are released.',
      ],
      [
        'Your balance',
        'PageHop keeps 20% of the item sale price. Shipping and taxes are not included in the commission.',
      ],
    ],
  ],
  'seller-faqs': [
    'Seller FAQs',
    'Quick answers for common selling questions.',
    [
      [
        'How much does it cost to list?',
        'Listing is free. PageHop keeps 20% of the item sale price when an item sells. Shipping and taxes are not included in the commission.',
      ],
      [
        'Who pays shipping?',
        'The buyer pays the shipping cost as part of their order. Sellers handle fulfillment: pack the item securely, ship it using the applicable method, and add tracking when available.',
      ],
      [
        'When do I get paid?',
        "Once an order is marked delivered, sale proceeds are held for 48 hours. If no issue is raised, earnings are released through PageHop's payout system.",
      ],
      [
        'How should I ship my book?',
        'Pack the item carefully, check the selected shipping details on the order, send the parcel, and add tracking when the selected shipping service supports it.',
      ],
      [
        'What should I include in a listing?',
        'Use clear photos of the actual item and include the edition, condition, included extras, price, and shipping details that matter to a buyer.',
      ],
    ],
  ],
  trust: [
    'Trust & Safety',
    'Accurate listings and on-platform records help buyers and sellers transact with confidence.',
    [
      ['Marketplace records', 'Keep checkout, messages, and transaction details on PageHop.'],
      [
        'Listing accuracy',
        'Review seller information, reviews, photos, condition disclosures, and edition details.',
      ],
      [
        'Concerns',
        'For order problems, message the other person through PageHop first when it’s appropriate, then report unresolved issues from the transaction page. Report harassment, threats, fraud, or other safety concerns to PageHop right away.',
      ],
    ],
  ],
  account: [
    'Account & Profile',
    'Keep public Shop information useful and private account details secure.',
    [
      [
        'Public profile',
        'Your profile photo, display name, and public Shop information can be visible to other users.',
      ],
      ['Shop settings', 'Use Profile Settings to update public Shop information.'],
      [
        'Private account details',
        'Manage contact details, password, payout setup, and available account preferences separately.',
      ],
    ],
  ],
};

const RelatedTopics = ({ topic }) => (
  <section className={css.explore}>
    <h2>Explore another topic</h2>
    <div>
      {topics
        .filter(([slug]) => slug !== topic)
        .slice(0, 3)
        .map(([slug, title, description]) => (
          <NamedLink key={slug} name="HowToUseShelfyTopicPage" params={{ topic: slug }}>
            <b>{title}</b>
            <span>{description}</span>
            <i aria-hidden="true">&rarr;</i>
          </NamedLink>
        ))}
    </div>
  </section>
);

const sellerResourceDetails = {
  payments: {
    Guide: FeesGuide,
    calloutTitle: 'Simple fees. No surprises.',
    calloutCopy: 'You only pay when your item sells.',
    railItems: ['Simple fees', 'Quick example', 'What the fee covers'],
    keyFact: {
      title: 'Seller fee',
      copy: 'PageHop keeps 20% of the item sale price when an item sells.',
    },
  },
  shipping: {
    Guide: ShippingGuide,
    calloutTitle: 'Pack carefully. Ship clearly. Communicate early.',
    calloutCopy: 'Clear shipping details help a good sale stay on track.',
    railItems: ['Buyer pays shipping', 'After a sale', 'Packing tips'],
    keyFact: {
      title: 'One rule to remember',
      copy: 'The buyer pays shipping. The seller handles fulfillment.',
    },
  },
  condition: {
    Guide: ConditionGuide,
    calloutTitle: 'Describe condition clearly and honestly.',
    calloutCopy: 'A clear listing makes it easier for buyers to choose with confidence.',
    railItems: ['Condition levels', 'Grade down', 'Practical guidance'],
    keyFact: {
      title: 'Condition standard',
      copy: 'Clear condition notes help buyers understand the actual item.',
    },
  },
  'getting-paid': {
    Guide: GettingPaidGuide,
    calloutTitle: 'Payouts are simple once delivery is confirmed.',
    calloutCopy:
      'Keep your payout details up to date so your earnings can move when the hold ends.',
    railItems: ['Delivered to payout', 'Payout details', 'Funds released'],
    keyFact: {
      title: 'Release timing',
      copy: 'Earnings are released after delivery and the 48-hour hold.',
    },
  },
  'seller-faqs': {
    Guide: SellerFAQGuide,
    calloutTitle: 'Still have questions? Start here.',
    calloutCopy: 'The essentials are easier to manage when everything is in one place.',
    railItems: ['Listings', 'Shipping', 'Fees', 'Payouts'],
    keyFact: { title: 'Quick answers', copy: 'Grouped FAQs keep the selling basics easy to scan.' },
  },
  'seller-shops': {
    Guide: ShopGuide,
    calloutTitle: 'Keep your shop current.',
    calloutCopy: 'A clear profile and current listings help buyers browse with confidence.',
    railItems: ['Shop profile', 'What to set up', 'What buyers see'],
    keyFact: {
      title: 'Shop setup',
      copy: 'Profile Settings is where you manage your public shop details.',
    },
  },
};

const SellerResourcePage = ({ topic, title, intro }) => {
  const { Guide, calloutTitle, calloutCopy, railItems, keyFact } = sellerResourceDetails[topic];

  return (
    <Page title={`${title} | PageHop Books`} description={intro}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={`${css.root} ${css.resourcePage}`}>
          <div className={css.content}>
            <BackNavigation name="SellerHubPage" label="Back to PageHop 101" />
            <SellerResourceHeader title={title} description={intro} />
            <SellerResourceLayout topic={topic} railItems={railItems} keyFact={keyFact}>
              <Guide />
            </SellerResourceLayout>
            <SellerResourceCTA title={calloutTitle} copy={calloutCopy} />
          </div>
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

const TopicPage = ({ topic }) => {
  const [title, intro, sections] = articles[topic] || articles.buying;
  if (topic === 'selling') {
    return <SellingOverview title={title} intro={intro} />;
  }
  if (sellerResourceDetails[topic]) {
    return <SellerResourcePage topic={topic} title={title} intro={intro} />;
  }
  const guide = (
    <article className={css.article}>
      {sections.map(([heading, copy]) => (
        <section key={heading}>
          <h2>{heading}</h2>
          <p>{copy}</p>
        </section>
      ))}
      {topic === 'buying' ? <WhenSomethingGoesWrong /> : null}
    </article>
  );
  return (
    <Page title={`${title} | PageHop Books`}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.root}>
          <div className={css.content}>
            <BackNavigation name="SellerHubPage" label="Back to PageHop 101" />
            <nav className={css.breadcrumb}>
              <NamedLink name="SellerHubPage">PageHop 101</NamedLink>
              <span> / {title}</span>
            </nav>
            <header className={css.intro}>
              <h1>{title}</h1>
              <p>{intro}</p>
            </header>
            {guide}
            {topic === 'iso' ? (
              <NewListingLink className={css.primaryAction} search="?listingType=iso-book">
                Post an ISO
              </NewListingLink>
            ) : null}
            <RelatedTopics topic={topic} />
          </div>
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

const HowToUseShelfyPage = ({ topic }) =>
  topic ? (
    <TopicPage topic={topic} />
  ) : (
  <Page
      title="PageHop 101 | PageHop Books"
      description="Learn how to browse, buy, sell, and manage books and bookish goods on PageHop."
  >
    <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
      <main className={css.root}>
        <div className={css.content}>
          <header className={css.intro}>
              <p className={css.eyebrow}>PAGEHOP 101</p>
              <h1>PageHop 101</h1>
            <p>
                Everything you need to buy, sell, manage your shop, and find your way around
                PageHop.
            </p>
            <div className={css.actions}>
              <NamedLink className={css.secondaryAction} name="SearchPage">
                Browse Books
              </NamedLink>
              <NewListingLink className={css.primaryAction}>Start Selling</NewListingLink>
            </div>
          </header>

            <section className={css.panelGrid} aria-label="PageHop 101 topics">
              {topics.map(([slug, title, description, cue]) => (
                <NamedLink
                  key={slug}
                  className={`${css.panel} ${css[slug]}`}
                  name="HowToUseShelfyTopicPage"
                  params={{ topic: slug }}
                >
                  <div>
                    <h2>{title}</h2>
                    <p>{description}</p>
                    <b>Learn how {cue.toLowerCase()} works &rarr;</b>
                  </div>
                </NamedLink>
              ))}
            </section>
        </div>
      </main>
    </LayoutSingleColumn>
  </Page>
);

export default HowToUseShelfyPage;
export { topics, articles };